import type { BriefingChange, BriefingPhoto } from '@reditelji/shared';
import type { BriefingLang } from './copy';

export const TOKEN_KEY = 'rr_briefing_token';

export interface BriefingShift {
  id: string;
  postId: string;
  fromMin: number;
  toMin: number;
  state: string;
  meetMin: number | null;
  meetPlace: string;
  dayKey: string;
  dayLabel: string;
  date: string;
  postCode: string;
  postMicro: string;
  postSector: string;
  /** The card's heading (`briefingPostHeading`). */
  postTitle: string;
  postSub: string;
  postType: string;
  postUrgent: boolean;
  lat: number | null;
  lng: number | null;
  instructions: string;
  /** English made from the Slovenian as it stands; empty when there is none yet. */
  instructionsEn: string;
  instructionsEnManual: boolean;
  /** Photos of the post, cover first. Bytes come from /api/v1/photos/:key. */
  photos: BriefingPhoto[];
  /** A live shift row still stands behind this card; day-of actions need one. */
  live: boolean;
  /** Their answer to this shift, or null while it is still to give (SPEC §1.1F). */
  answer: { status: ShiftAnswerGiven; comment: string; at: string } | null;
  /** Until the shift's first minute. */
  answerable: boolean;
  /** Still theirs in the working state: a refusal or a proposed change needs it. */
  held: boolean;
}

export type ShiftAnswerGiven = 'ok' | 'change' | 'cant';

export type ConfirmationStatus = 'potrjen' | 'čaka' | 'zavrnjen';

export interface BriefingPayload {
  name: string;
  lang: BriefingLang;
  scheduleVersion: number;
  confirmationStatus: ConfirmationStatus;
  confirmationComment: string;
  event: {
    name: string;
    opsName: string;
    opsPhone: string;
    mailbox: string;
    whatsappName: string;
    whatsappUrl: string;
    /** The organisers' emergency number; '' hides every line that needs it. */
    emergencyPhone: string;
    /** ISO date, or null when no deadline is set. */
    answerDeadline: string | null;
  };
  shifts: BriefingShift[];
  checkins: { shiftId: string; kind: 'in' | 'out'; atMin: number }[];
  briefingSentAt: string | null;
  /** False before the first publication — the shift list is replaced. */
  published: boolean;
  /** When the shown schedule last changed; drives the stamp under the greeting. */
  changedAt: string | null;
  changes: BriefingChange[];
  needsReconfirm: boolean;
  /**
   * Marked as not coming (SPEC §1.1K): the page says so plainly, keeps the
   * contacts and general information, and asks for nothing.
   */
  cancelled: boolean;
}

/** An answer the API refused, carrying the sentence it gave. */
export class BriefingApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function bfApi<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${token}`);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  const res = await fetch(`/api/v1${path}`, { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new BriefingApiError(res.status, body?.error?.message ?? String(res.status));
  }
  return (await res.json()) as T;
}
